import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Formik, Form, Field } from 'formik';
import Grid from 'apollo-react/components/Grid';
import SwitchInput from '../../common/Form/switchInput';
import { renderInputComponent } from '../../common/Form/renderInputComponent';
import Button from '../../common/Button';
import CommonModal from '../../common/CommonModal';
import styles from './form.module.css';
import useUnsavedChangesWarning from '../../../hooks/ui/navigation/useUnsavedChangesWarning';

const DynamicForm = ({ persona }) => {
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: true,
    is_shown_on_sidebar: false,
  });
  const [formDataCopy, setFormDataCopy] = useState({
    name: '',
    description: '',
    status: true,
    is_shown_on_sidebar: false,
  });
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resp, setResp] = useState('');
  const [token, setToken] = useState('');
  const [t] = useTranslation();
  const { id } = useParams();
  const [initialValues, setInitialValues] = useState({});
  const [discardModal, setDiscardModal] = useState(false);

  const isDirty = useMemo(() => {
    return (
      (formData.name || '') !== (formDataCopy.name || '') ||
      (formData.description || '') !== (formDataCopy.description || '') ||
      formData.status !== formDataCopy.status ||
      formData.is_shown_on_sidebar !== formDataCopy.is_shown_on_sidebar
    );
  }, [formData, formDataCopy]);

  const { navigateBack } = useUnsavedChangesWarning(isDirty);

  // Fetch form fields and data (if updating) from the backend
  useEffect(() => {
    const fetchFormMetadata = async () => {
      try {
        const formFields = [
          {
            name: 'name',
            type: 'text',
            label: 'Category Name',
            required: true,
          },
          {
            name: 'description',
            type: 'text',
            label: 'Description',
            required: false,
          },
        ];
        setFormFields(formFields);
        if (id) {
          // If we have an ID, fetch data for updating
          setIsUpdate(true);
          const { data: recordData } = await axiosInstance.get(
            `/api/categories/${id}`,
            {
              headers: {
                authorization: token,
              },
            }
          );

          setFormData({
            ...recordData,
            status: recordData.status === 'published_active',
            is_shown_on_sidebar: recordData.is_shown_on_sidebar || false,
          });
          setFormDataCopy({
            ...recordData,
            status: recordData.status === 'published_active',
            is_shown_on_sidebar: recordData.is_shown_on_sidebar || false,
          });
        }
        // const { data: fieldDefinitions } = await axiosInstance.get('/api/form-fields');
        // setFormFields(fieldDefinitions);

        setLoading(false);
      } catch (err) {
        setResp(t('Error fetching form metadata or data'));
        setLoading(false);
      }
    };

    fetchFormMetadata();
  }, [id, t]);

  useEffect(() => {
    const initialValues = formFields.reduce((acc, field) => {
      acc[field.name] = formData[field.name] || '';
      return acc;
    }, {});

    setInitialValues(initialValues);
  }, [formFields, formData]);

  // Handle input changes
  const handleChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    const updatedFormData = {
      ...formData,
      status: formData['status'] ? 'published_active' : 'not_published',
      is_shown_on_sidebar: formData['is_shown_on_sidebar'] || false,
    };
    try {
      if (isUpdate) {
        await axiosInstance.put(`/api/categories/${id}`, updatedFormData, {
          withCredentials: true,
        });
        window.dispatchEvent(new CustomEvent('sidebarCategoriesUpdated'));
        toast.success('Category Updated Successfully!', {
          position: 'top-center',
        });
        navigateBack();
      } else {
        const resp = await axiosInstance.post(
          '/api/categories/create',
          updatedFormData
        );
        window.dispatchEvent(new CustomEvent('sidebarCategoriesUpdated'));
        toast.success(t('Category Added Successfully!'), {
          position: 'top-center',
        });
        setToken('');
        if (resp.data.token) {
          setToken(resp.data.token);
        } else {
          navigateBack();
        }
      }
    } catch (err) {
      const msg = isUpdate
        ? t('Error updating form')
        : t('Error creating form');
      setResp(msg);
      toast.error(msg, { position: 'top-center' });
      return false;
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className={`content ${styles.formContainer}`}>
      {/* {resp && <p>{resp}</p>} */}
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit, setFieldValue }) => (
              <Form onSubmit={handleSubmit}>
                {formFields.map((field) => (
                  <Field key={field.name} name={field.name}>
                    {({ field: formikField, meta }) => {
                      const handleFieldChange = (value) => {
                        console.log('formikField', formikField, value);
                        setFieldValue(formikField.name, value);
                        handleChange(formikField.name, value);
                      };

                      let updatedFormikField = formikField;
                      if (field.name === 'description')
                        updatedFormikField = {
                          ...updatedFormikField,
                          multiline: true,
                          textareaWidth: '100%',
                        };

                      return renderInputComponent(
                        field,
                        formData,
                        updatedFormikField,
                        meta,
                        handleFieldChange
                      );
                    }}
                  </Field>
                ))}
                {!isUpdate ? (
                  <Field name={'status'}>
                    {({ field: formikField, meta }) => (
                      <SwitchInput
                        {...formikField}
                        meta={meta}
                        name={'status'}
                        onChange={(checked) => handleChange('status', checked)}
                        label={t('publish')}
                        value={formData['status']}
                        className={styles.switchInput}
                      />
                    )}
                  </Field>
                ) : null}
                <Field name={'is_shown_on_sidebar'}>
                  {({ field: formikField, meta }) => (
                    <SwitchInput
                      {...formikField}
                      meta={meta}
                      name={'is_shown_on_sidebar'}
                      onChange={(checked) =>
                        handleChange('is_shown_on_sidebar', checked)
                      }
                      label={t('show_on_sidebar')}
                      value={formData['is_shown_on_sidebar']}
                      className={styles.switchInput}
                    />
                  )}
                </Field>
                <div className={styles.buttonContainer}>
                  <Button
                    color="primary"
                    onClick={() => setDiscardModal(true)}
                    className={styles.discardButton}
                    text={t('Discard')}
                  />

                  <Button
                    color="primary"
                    type="submit"
                    className={styles.formButton}
                    text={t('Save')}
                  />
                </div>
              </Form>
            )}
          </Formik>
          {token && (
            <div className="mt-2 p-2 card">
              <h6>Generated Token:</h6>
              <p className="">{token}</p>
            </div>
          )}
        </Grid>
      </Grid>
      <CommonModal
        open={discardModal}
        message="Are you sure you want to discard your changes?"
        buttonProps={[
          {
            label: 'Cancel',
            onClick: () => {
              setDiscardModal(false);
            },
          },
          {
            label: 'Confirm',
            onClick: () => {
              setDiscardModal(false);
              setFormData(formDataCopy);
            },
          },
        ]}
        hideHeader
        id="hideHeader"
      />
    </div>
  );
};

export default DynamicForm;
